/**
 * Контроллер вида интерактивной гиперболической карты: навигация перетаскиванием
 * (pan) и непрерывность поверхности через соседние копии фундаментальной области.
 *
 * Вид хранится как изометрия диска `V` (матрица SU(1,1)). Точка поверхности `z`
 * отображается рендерером в позицию `V(z)` (см. {@link module:webgl-map-renderer}).
 * Перетаскивание мышью применяет к `V` изометрию диска, сохраняющую модель
 * (Требование 4.1): «схваченная» точка поверхности следует за курсором.
 *
 * Когда центр вида уходит за пределы одной фундаментальной области, поверхность
 * продолжается соседней копией через преобразование склейки (образующую фуксовой
 * группы `γ`). Нормализация {@link MapViewController.normalizeView} заменяет
 * `V → V∘γ`, возвращая центр вида в фундаментальную область; поскольку `γ` —
 * элемент группы склейки, а замощение и содержимое периодичны по `Γ`,
 * изображение остаётся визуально непрерывным (Требование 4.2).
 *
 * Математическая часть (`screenToDisk`, `diskToScreen`, `pan`, `normalizeView`,
 * `findNearestTile`) чистая и не зависит от DOM — покрывается юнит-тестами.
 * DOM-взаимодействие ({@link MapViewController.attach}) — тонкая опциональная
 * обёртка, вызывающая те же чистые методы и колбэк `onChange`.
 *
 * Зум колесом мыши плавно меняет масштаб отображения (Требование 4.3) в чистом
 * методе {@link MapViewController.zoomAt}. Масштаб зажимается в диапазон
 * `[minScale, maxScale]`:
 * - `minScale` (максимальное отдаление) — масштаб, при котором фундаментальный
 *   многоугольник вписан в видимую область: его циркумрадиус (наибольший
 *   евклидов радиус вершины) равен видимой полу-протяжённости. Дальше отдалиться
 *   нельзя (Требование 4.4).
 * - `maxScale` (максимальное приближение) — выводится из параметра масштаба карты
 *   через число уровней детализации (Требование 4.5).
 *
 * Требования: 4.1, 4.2, 4.3, 4.4, 4.5
 */

import type {
  Complex,
  FundamentalPolygon,
  Geometry,
  GeometryType,
  Isometry,
  Mobius,
  SidePairingResult,
  SurfacePairing,
} from '@langton/shared';
import {
  asIsometry,
  compose,
  createGeometry,
  enumerateNeighbors,
  enumerateSurfaceNeighbors,
  isometryIdentity,
  maxLevelForScale,
  normalize,
  translateFromOrigin,
  translateToOrigin,
} from '@langton/shared';

/** Центр диска (начало координат) — точка, отображаемая в центр холста. */
const ORIGIN: Complex = { re: 0, im: 0 };

/** Размеры холста в пикселях, необходимые для перевода координат экран↔диск. */
export interface Viewport {
  /** Ширина холста в пикселях (`> 0`). */
  width: number;
  /** Высота холста в пикселях (`> 0`). */
  height: number;
}

/** Точка в экранных (пиксельных) координатах холста: `(0, 0)` — верхний левый угол. */
export interface ScreenPoint {
  x: number;
  y: number;
}

/** Результат поиска ближайшей плитки замощения (соседней копии области). */
export interface NearestTile {
  /** Изометрия `γ`, образ которой `γ(F)` содержит запрошенную точку поверхности. */
  transform: Mobius;
  /** Индекс `γ` в списке соседей ({@link MapViewController.getNeighbors}). */
  index: number;
  /** `true`, если ближайшая плитка — сама фундаментальная область (тождество). */
  isIdentity: boolean;
}

/** Опции конфигурации {@link MapViewController}. */
export interface MapViewControllerOptions {
  /**
   * Тип геометрии карты (Требование 6). Определяет навигационную изометрию
   * «захвата» в {@link MapViewController.pan}: для евклидовой и сферической карт
   * применяется соответствующая изометрия геометрии, сохраняющая модель с
   * допуском `1e-6` (Требование 6.7). По умолчанию `'hyperbolic'` — поведение
   * идентично прежнему (обратная совместимость). Игнорируется, если явно передан
   * {@link MapViewControllerOptions.geometry}.
   */
  geometryType?: GeometryType;
  /**
   * Явная реализация геометрии для навигации (Требование 6). Имеет приоритет над
   * {@link MapViewControllerOptions.geometryType}. По умолчанию выбирается по
   * `geometryType` через `createGeometry` (гиперболическая, если не задано).
   */
  geometry?: Geometry;
  /**
   * Масштаб отображения диска: во сколько единиц клип-координат укладывается
   * единичный радиус (согласуется с `WebGLMapRenderer.setScale`). `1` вписывает
   * весь диск в видимую область. По умолчанию `1`.
   */
  scale?: number;
  /**
   * Параметр масштаба карты (число уровней детализации), задаваемый при её
   * создании. Определяет верхнюю границу приближения `maxScale` через
   * {@link maxLevelForScale} (Требование 4.5). По умолчанию `4` (значение по
   * умолчанию поля `scale` карты в БД).
   */
  mapScale?: number;
  /**
   * Множитель максимального отдаления (Требование 2.3): во сколько раз область
   * обзора при предельном отдалении может превышать одну фундаментальную область.
   * Задаёт минимальный масштаб `minScale = 1 / (r · maxZoomOut)` (см.
   * {@link minScaleForPolygon}). Допустимый диапазон карты — `[0.1, 100.0]`
   * (валидируется в админке и на сервере; нижняя граница расширена вниз до `0.1`
   * — Требование 9.1). По умолчанию `1` — видна ровно одна фундаментальная
   * область, поведение идентично прежнему (Требование 2.5). При `maxZoomOut < 1`
   * предельное отдаление показывает менее одной фундаментальной области
   * (`minScale > 1/r`), а начальный вид — её фрагмент (Требование 9.4).
   */
  maxZoomOut?: number;
  /**
   * Отдельный множитель максимального отдаления для авторизованного
   * администратора (Требование 11). Применяется вместо {@link maxZoomOut}, когда
   * включён админ-режим отдаления ({@link MapViewController.setAdminZoom} с
   * `true`): предел отдаления пересчитывается как
   * `minScale = 1 / (r · adminMaxZoomOut)`. По требованию `adminMaxZoomOut ≥
   * maxZoomOut`, поэтому авторизованный администратор может отдалиться не ближе
   * обычного зрителя (`minScale_admin ≤ minScale_public`, Требование 11.3). По
   * умолчанию совпадает с {@link maxZoomOut} (при его отсутствии — `1`), так что
   * без явного значения переключение админ-режима не меняет предел (обратная
   * совместимость, Требование 11.6).
   */
  adminMaxZoomOut?: number;
  /**
   * Глубина перечисления соседних плиток (BFS по образующим фуксовой группы),
   * если во второй параметр конструктора передан {@link SidePairingResult}.
   * По умолчанию `3`.
   */
  neighborDepth?: number;
  /** Ограничение на число соседних плиток при перечислении. По умолчанию `128`. */
  maxNeighbors?: number;
  /**
   * Максимальное число итераций нормализации вида за один вызов
   * {@link MapViewController.normalizeView}. По умолчанию `8`.
   */
  maxNormalizeIterations?: number;
  /**
   * Колбэк, вызываемый при изменении вида `V` (после `pan`/`normalizeView`
   * в DOM-обработчиках). Обычно перерисовывает карту с новым видом.
   */
  onChange?: (view: Mobius) => void;
  /**
   * Колбэк, вызываемый при изменении масштаба отображения колесом мыши
   * (Требование 4.3). Обычно вызывает `WebGLMapRenderer.setScale` с новым
   * значением и перерисовывает карту.
   */
  onScaleChange?: (scale: number) => void;
}

/** Значения по умолчанию для опций контроллера. */
const DEFAULTS = {
  scale: 1,
  mapScale: 4,
  neighborDepth: 3,
  maxNeighbors: 128,
  maxNormalizeIterations: 8,
} as const;

/**
 * Чувствительность зума колесом: масштаб умножается на `exp(-deltaY · k)` на
 * каждое событие `wheel`. Небольшое `k` даёт плавное непрерывное изменение
 * масштаба (Требование 4.3).
 */
const ZOOM_WHEEL_SENSITIVITY = 0.0015;

/**
 * Евклидов циркумрадиус фундаментального многоугольника в модели Пуанкаре:
 * наибольший евклидов радиус его вершины (`max |v|`).
 *
 * @param polygon фундаментальный многоугольник (вершины в координатах диска)
 * @returns наибольший евклидов радиус вершины (`0`, если вершин нет)
 */
export function fundamentalCircumRadius(polygon: FundamentalPolygon): number {
  let max = 0;
  for (const v of polygon.vertices) {
    const r = Math.hypot(v.re, v.im);
    if (r > max) {
      max = r;
    }
  }
  return max;
}

/**
 * Минимальный масштаб отображения (максимальное отдаление): масштаб, при котором
 * в видимую область помещается ровно `maxZoomOut` фундаментальных областей по
 * меньшей оси (Требования 2.3, 2.5, 4.4).
 *
 * Видимая полу-протяжённость по меньшей оси холста в единицах диска равна
 * `1 / scale` (следует из проекции рендерера, см. {@link MapViewController.diskToScreen}).
 * Приравнивая её к `maxZoomOut · r` (циркумрадиус `r` многоугольника, умноженный
 * на множитель отдаления), получаем `minScale = 1 / (r · maxZoomOut)`: при
 * `maxZoomOut = 1` в кадр вписана ровно одна фундаментальная область (поведение
 * по умолчанию, идентичное прежнему — Требование 2.5); при `maxZoomOut = k`
 * позволяется отдалиться в `k` раз дальше, вместив примерно `k` областей.
 *
 * Допустимый диапазон карты — `[0.1, 100.0]` (нижняя граница расширена вниз до
 * `0.1` — Требование 9.1); формула не меняется и уже корректна для
 * `maxZoomOut ∈ (0, 1)`. При `maxZoomOut < 1` получаем `minScale > 1/r`: предельное
 * отдаление показывает менее одной фундаментальной области, а начальный вид
 * (стартует с `minScale`) — соответственно её фрагмент, при этом исключение не
 * выбрасывается (Требование 9.4).
 *
 * @param polygon фундаментальный многоугольник
 * @param maxZoomOut множитель максимального отдаления (Требования 2.3, 9.1). По
 *   умолчанию `1` (одна фундаментальная область). Некорректные значения
 *   (не число, `≤ 0`) трактуются как `1` (фолбэк, Требование 2.5).
 * @returns минимальный (наиболее отдалённый) допустимый масштаб
 */
export function minScaleForPolygon(
  polygon: FundamentalPolygon,
  maxZoomOut = 1,
): number {
  const r = fundamentalCircumRadius(polygon);
  const m = Number.isFinite(maxZoomOut) && maxZoomOut > 0 ? maxZoomOut : 1;
  return r > 0 ? 1 / (r * m) : DEFAULTS.scale;
}

/**
 * Максимальный масштаб отображения (максимальное приближение), выводимый из
 * параметра масштаба карты (Требование 4.5).
 *
 * Каждый уровень детализации удваивает эффективное приближение (сторона
 * треугольника делится пополам при подразбиении), поэтому диапазон приближения
 * от максимального отдаления составляет `2^maxLevel`, где
 * `maxLevel = maxLevelForScale(mapScale)`. Отсюда
 * `maxScale = minScale · 2^maxLevel`.
 *
 * @param minScale минимальный масштаб ({@link minScaleForPolygon})
 * @param mapScale параметр масштаба карты (число уровней детализации)
 * @returns максимальный (наиболее приближённый) допустимый масштаб
 */
export function maxScaleForMap(minScale: number, mapScale: number): number {
  return minScale * Math.pow(2, maxLevelForScale(mapScale));
}

/**
 * Построить изометрию диска, переводящую точку `p0` в точку `p1`.
 *
 * Используется композиция «перенос `p0` в центр, затем центр в `p1`»:
 * `T = translateFromOrigin(p1) ∘ translateToOrigin(p0)`, откуда `T(p0) = p1`.
 * Это сохраняющее модель преобразование (Требование 4.1), задающее
 * гиперболический «захват и перетаскивание»: схваченная точка следует за
 * курсором вдоль геодезической.
 *
 * @param p0 исходная точка диска (|p0| < 1)
 * @param p1 целевая точка диска (|p1| < 1)
 * @returns изометрия `T` с `T(p0) = p1`
 */
export function diskIsometryMapping(p0: Complex, p1: Complex): Mobius {
  return normalize(compose(translateFromOrigin(p1), translateToOrigin(p0)));
}

/**
 * Перевести пиксельные координаты холста в точку диска в кадре вида
 * (`w = V(z)`, а не в координаты поверхности). Чистая функция, обратная
 * проекции вершинного шейдера рендерера: пиксель → клип-координаты (с
 * переворотом оси Y) → деление на масштаб с поправкой на соотношение сторон.
 *
 * Вынесена из {@link MapViewController.screenToDisk} как единый источник истины,
 * переиспользуемый слоем рисования (перевод ввода экран→поверхность, `V⁻¹`).
 *
 * @param px координата X в пикселях (`0` — левый край)
 * @param py координата Y в пикселях (`0` — верхний край)
 * @param viewport размеры холста в пикселях
 * @param scale масштаб отображения диска
 * @returns точка диска в кадре вида
 */
export function projectScreenToDisk(
  px: number,
  py: number,
  viewport: Viewport,
  scale: number,
): Complex {
  const { width, height } = viewport;
  // Пиксель → нормализованные клип-координаты [-1, 1] с переворотом оси Y.
  const clipX = (px / width) * 2 - 1;
  const clipY = 1 - (py / height) * 2;
  const aspect = height > 0 ? width / height : 1;
  // Обратная проекция: клип → точка диска (см. вершинный шейдер рендерера).
  let x: number;
  let y: number;
  if (aspect >= 1) {
    x = (clipX * aspect) / scale;
    y = clipY / scale;
  } else {
    x = clipX / scale;
    y = clipY / (aspect * scale);
  }
  return { re: x, im: y };
}

/**
 * Перевести точку диска в кадре вида (`w = V(z)`) в пиксельные координаты
 * холста. Обратная к {@link projectScreenToDisk}. Чистая функция, единый
 * источник истины для {@link MapViewController.diskToScreen} и слоя рисования.
 *
 * @param w точка диска в кадре вида
 * @param viewport размеры холста в пикселях
 * @param scale масштаб отображения диска
 */
export function projectDiskToScreen(
  w: Complex,
  viewport: Viewport,
  scale: number,
): ScreenPoint {
  const { width, height } = viewport;
  const aspect = height > 0 ? width / height : 1;
  let clipX: number;
  let clipY: number;
  if (aspect >= 1) {
    clipX = (w.re * scale) / aspect;
    clipY = w.im * scale;
  } else {
    clipX = w.re * scale;
    clipY = w.im * scale * aspect;
  }
  return {
    x: ((clipX + 1) / 2) * width,
    y: ((1 - clipY) / 2) * height,
  };
}

/**
 * Контроллер вида и навигации по гиперболической карте.
 *
 * Жизненный цикл:
 * 1. `new MapViewController(polygon, neighbors, options)` — задаёт геометрию,
 *    соседние копии (для непрерывности) и параметры.
 * 2. `screenToDisk` / `diskToScreen` — перевод координат экран↔диск (обратный
 *    проекции рендерера с учётом масштаба, соотношения сторон и размера холста).
 * 3. `pan(p0, p1)` — применяет изометрию «захвата», чтобы точка `p0` перешла
 *    в `p1` (Требование 4.1).
 * 4. `normalizeView()` — возвращает центр вида в фундаментальную область через
 *    соседнюю копию (Требование 4.2).
 * 5. `attach(canvas)` / `detach()` — опциональные DOM-обработчики мыши.
 */
export class MapViewController {
  private readonly polygon: FundamentalPolygon;
  /** Соседние копии как обобщённые изометрии (матрица + признак обращения). */
  private readonly neighbors: Isometry[];
  private readonly options: Required<
    Omit<
      MapViewControllerOptions,
      | 'onChange'
      | 'onScaleChange'
      | 'geometryType'
      | 'geometry'
      | 'maxZoomOut'
      | 'adminMaxZoomOut'
    >
  > & {
    onChange?: (view: Mobius) => void;
    onScaleChange?: (scale: number) => void;
  };

  /**
   * Геометрия карты для навигации (Требование 6): задаёт изометрию «захвата»
   * в {@link pan}. По умолчанию гиперболическая — поведение прежнее.
   */
  private readonly geometry: Geometry;

  /**
   * Текущий вид `V` как обобщённая изометрия (матрица + признак обращения
   * ориентации). По умолчанию тождество. Для неориентируемой поверхности вид
   * может стать антиголоморфным после пересечения обращающей ориентацию склейки
   * (Требование 8): тогда `orientationReversing = true` и рендер даёт зеркальное
   * продолжение.
   */
  private viewIso: Isometry = isometryIdentity();
  /** Текущий масштаб отображения диска (зеркалит `WebGLMapRenderer`). */
  private scale: number;
  /**
   * Минимальный масштаб (максимальное отдаление, Требование 4.4). Изменяем:
   * переключение админ-режима отдаления ({@link setAdminZoom}) пересчитывает его
   * из соответствующего множителя (Требование 11).
   */
  private minScale: number;
  /**
   * Максимальный масштаб (максимальное приближение, Требование 4.5). Изменяем:
   * выводится из {@link minScale}, поэтому пересчитывается вместе с ним при
   * смене админ-режима отдаления ({@link setAdminZoom}, Требование 11).
   */
  private maxScale: number;
  /**
   * Публичный множитель максимального отдаления ({@link maxZoomOut}), задающий
   * предел отдаления для неавторизованного пользователя (Требование 11.2).
   */
  private readonly publicMaxZoomOut: number;
  /**
   * Админский множитель максимального отдаления ({@link adminMaxZoomOut}),
   * задающий предел отдаления для авторизованного администратора (Требование
   * 11.3). По умолчанию совпадает с {@link publicMaxZoomOut}.
   */
  private readonly adminMaxZoomOut: number;
  /**
   * Активен ли админ-режим отдаления (Требование 11). Переключается
   * {@link setAdminZoom} по признаку авторизации; по умолчанию `false`
   * (публичный предел).
   */
  private adminZoom = false;

  // Состояние DOM-перетаскивания.
  private canvas: HTMLCanvasElement | null = null;
  private dragging = false;
  /** Схваченная точка поверхности (в координатах области, `V⁻¹` от точки клика). */
  private grabSurface: Complex | null = null;
  private readonly pointerDownHandler = (e: PointerEvent): void => this.onPointerDown(e);
  private readonly pointerMoveHandler = (e: PointerEvent): void => this.onPointerMove(e);
  private readonly pointerUpHandler = (e: PointerEvent): void => this.onPointerUp(e);
  private readonly wheelHandler = (e: WheelEvent): void => this.onWheel(e);

  /**
   * @param polygon фундаментальный многоугольник (вершины в координатах диска)
   * @param neighbors соседние копии области: массив изометрий, либо
   *   {@link SidePairingResult} для автоматического перечисления, либо `null`
   *   (только сама область — нормализация будет тривиальной)
   * @param options параметры масштаба, перечисления соседей и колбэка
   */
  constructor(
    polygon: FundamentalPolygon,
    neighbors?: Mobius[] | Isometry[] | SidePairingResult | SurfacePairing | null,
    options: MapViewControllerOptions = {},
  ) {
    this.polygon = polygon;
    // Геометрия навигации: явная реализация имеет приоритет, иначе выбирается по
    // типу (по умолчанию гиперболическая — обратная совместимость).
    this.geometry = options.geometry ?? createGeometry(options.geometryType ?? 'hyperbolic');
    this.options = {
      scale: options.scale ?? DEFAULTS.scale,
      mapScale: options.mapScale ?? DEFAULTS.mapScale,
      neighborDepth: options.neighborDepth ?? DEFAULTS.neighborDepth,
      maxNeighbors: options.maxNeighbors ?? DEFAULTS.maxNeighbors,
      maxNormalizeIterations:
        options.maxNormalizeIterations ?? DEFAULTS.maxNormalizeIterations,
      onChange: options.onChange,
      onScaleChange: options.onScaleChange,
    };
    // Множители отдаления: публичный (maxZoomOut) и админский (adminMaxZoomOut,
    // Требование 11). Оба сохраняются, чтобы setAdminZoom мог пересчитывать
    // предел без пересоздания контроллера. Админский по умолчанию совпадает с
    // публичным (при отсутствии обоих — 1), поэтому без явного adminMaxZoomOut
    // переключение админ-режима не меняет предел (Требование 11.6).
    this.publicMaxZoomOut = options.maxZoomOut ?? 1;
    this.adminMaxZoomOut = options.adminMaxZoomOut ?? this.publicMaxZoomOut;
    // Пределы зума: minScale — предел отдаления с учётом публичного множителя
    // maxZoomOut (Требования 2.3, 2.5, 4.4); фолбэк maxZoomOut = 1 сохраняет
    // прежнее поведение «одна область вписана». maxScale — из параметра масштаба
    // карты (Требование 4.5). Начинаем в публичном режиме (adminZoom = false),
    // пока не авторизован администратор (Требование 11.2).
    this.minScale = minScaleForPolygon(polygon, this.publicMaxZoomOut);
    this.maxScale = maxScaleForMap(this.minScale, this.options.mapScale);
    // Начальный масштаб хранится «как есть» (проекция); зажатие в пределы зума —
    // задача навигационного API ({@link setScaleClamped}/{@link zoomAt}).
    this.scale = this.options.scale;
    this.neighbors = this.resolveNeighbors(neighbors);
  }

  /** Текущий вид `V` (матрица линейной части; обратная совместимость). */
  getView(): Mobius {
    return this.viewIso.matrix;
  }

  /**
   * Текущий вид `V` как обобщённая изометрия (матрица + признак обращения
   * ориентации) — для рендера, честно учитывающего антиголоморфный (зеркальный)
   * вид неориентируемой поверхности (Требование 8).
   */
  getViewIsometry(): Isometry {
    return this.viewIso;
  }

  /** Геометрия карты (для слоёв, переводящих координаты через её действие). */
  getGeometry(): Geometry {
    return this.geometry;
  }

  /**
   * Установить вид `V` напрямую (без нормализации). Принимает матрицу
   * {@link Mobius} (трактуется как сохраняющий ориентацию вид) или обобщённую
   * {@link Isometry}.
   */
  setView(view: Mobius | Isometry): void {
    this.viewIso = asIsometry(view);
  }

  /** Текущий масштаб отображения диска. */
  getScale(): number {
    return this.scale;
  }

  /** Установить масштаб отображения диска (`> 0`), без зажатия в пределы зума. */
  setScale(scale: number): void {
    if (Number.isFinite(scale) && scale > 0) {
      this.scale = scale;
    }
  }

  /** Минимальный масштаб (максимальное отдаление, Требование 4.4). */
  getMinScale(): number {
    return this.minScale;
  }

  /** Максимальный масштаб (максимальное приближение, Требование 4.5). */
  getMaxScale(): number {
    return this.maxScale;
  }

  /** Активен ли админ-режим отдаления (Требование 11). */
  isAdminZoom(): boolean {
    return this.adminZoom;
  }

  /**
   * Переключить админ-режим отдаления по признаку авторизации (Требование 11).
   *
   * В админ-режиме (`enabled = true`) предел отдаления вычисляется из админского
   * множителя {@link adminMaxZoomOut}, иначе — из публичного {@link maxZoomOut}
   * (Требования 11.2, 11.3). Метод пересчитывает `minScale = minScaleForPolygon(
   * polygon, множитель)` и `maxScale = maxScaleForMap(minScale, mapScale)`, затем
   * повторно зажимает текущий масштаб в новый диапазон (`clampScale`) — так при
   * смене авторизации предел обновляется без перезагрузки карты, а текущий
   * масштаб не выходит за новые границы (Требование 11.4).
   *
   * Поскольку `adminMaxZoomOut ≥ maxZoomOut`, включение админ-режима не
   * увеличивает `minScale` (`minScale_admin ≤ minScale_public`), то есть
   * авторизованный администратор может отдалиться не ближе обычного зрителя.
   *
   * Если повторный клэмп изменил масштаб, вызывается `onScaleChange` (обновление
   * масштаба рендерера); в любом случае вызывается `onChange` для перерисовки с
   * новым пределом. Идемпотентен относительно значения `enabled`.
   *
   * @param enabled `true` — применить админский предел; `false` — публичный
   * @returns фактический (при необходимости зажатый) масштаб после пересчёта
   */
  setAdminZoom(enabled: boolean): number {
    this.adminZoom = enabled;
    const multiplier = enabled ? this.adminMaxZoomOut : this.publicMaxZoomOut;
    this.minScale = minScaleForPolygon(this.polygon, multiplier);
    this.maxScale = maxScaleForMap(this.minScale, this.options.mapScale);
    const previous = this.scale;
    this.scale = this.clampScale(this.scale);
    if (this.scale !== previous) {
      this.options.onScaleChange?.(this.scale);
    }
    this.options.onChange?.(this.viewIso.matrix);
    return this.scale;
  }

  /**
   * Зажать масштаб в допустимый диапазон `[minScale, maxScale]`. Некорректные
   * значения (не число, `≤ 0`) приводятся к `minScale`.
   *
   * @param scale запрошенный масштаб
   * @returns масштаб, зажатый в `[minScale, maxScale]`
   */
  clampScale(scale: number): number {
    if (!Number.isFinite(scale) || scale <= 0) {
      return this.minScale;
    }
    return Math.min(Math.max(scale, this.minScale), this.maxScale);
  }

  /**
   * Установить масштаб отображения, зажав его в пределы зума
   * `[minScale, maxScale]` (Требования 4.4, 4.5).
   *
   * @param scale запрошенный масштаб
   * @returns фактически установленный (зажатый) масштаб
   */
  setScaleClamped(scale: number): number {
    this.scale = this.clampScale(scale);
    return this.scale;
  }

  /**
   * Плавно изменить масштаб отображения умножением на `factor`, зажав результат
   * в `[minScale, maxScale]` (Требование 4.3). Отдалиться дальше одной
   * фундаментальной области (Требование 4.4) или приблизиться сильнее предела
   * масштаба карты (Требование 4.5) нельзя.
   *
   * Если заданы `screenPoint` и `viewport`, зум выполняется «к курсору»: точка
   * поверхности под курсором остаётся на месте. Пиксель курсора отображается в
   * точку кадра вида `wOld` при старом масштабе и `wNew` при новом; вид
   * подправляется так, чтобы отображаемая точка перешла из `wOld` в `wNew`
   * ({@link pan}), после чего вид нормализуется. Центрирование применяется только
   * когда обе точки лежат внутри диска (иначе изометрия «захвата» не определена).
   *
   * Метод чистый по отношению к DOM: меняет только внутренние `scale`/`view`.
   *
   * @param factor множитель масштаба (`> 1` — приблизить, `< 1` — отдалить)
   * @param screenPoint пиксель курсора для центрирования зума (опционально)
   * @param viewport размеры холста (обязателен вместе с `screenPoint`)
   * @returns фактически установленный (зажатый) масштаб
   */
  zoomAt(factor: number, screenPoint?: ScreenPoint, viewport?: Viewport): number {
    const oldScale = this.scale;
    if (!Number.isFinite(factor) || factor <= 0) {
      return oldScale;
    }
    const newScale = this.clampScale(oldScale * factor);
    if (newScale === oldScale) {
      return oldScale;
    }
    if (screenPoint && viewport) {
      const wOld = this.screenToDisk(screenPoint.x, screenPoint.y, viewport, oldScale);
      const wNew = this.screenToDisk(screenPoint.x, screenPoint.y, viewport, newScale);
      this.scale = newScale;
      // «Захват» точки под курсором в гиперболической модели определён лишь
      // внутри диска; для евклидовой/сферической геометрии изометрия «захвата»
      // определена всюду, поэтому ограничение снимается.
      const insideDisk =
        this.geometry.type !== 'hyperbolic' ||
        (Math.hypot(wOld.re, wOld.im) < 1 && Math.hypot(wNew.re, wNew.im) < 1);
      if (insideDisk) {
        this.pan(wOld, wNew);
        this.normalizeView();
      }
    } else {
      this.scale = newScale;
    }
    return this.scale;
  }

  /**
   * Соседние копии фундаментальной области (матрицы линейной части; обратная
   * совместимость). Для честного учёта обращения ориентации используйте
   * {@link getNeighborIsometries}.
   */
  getNeighbors(): Mobius[] {
    return this.neighbors.map((n) => n.matrix);
  }

  /** Соседние копии как обобщённые изометрии (матрица + признак обращения). */
  getNeighborIsometries(): Isometry[] {
    return this.neighbors;
  }

  /**
   * Перевести пиксельные координаты холста в точку диска в текущем кадре вида
   * (то есть в отображаемую точку `w = V(z)`, а не в координаты поверхности).
   *
   * Обращает проекцию вершинного шейдера рендерера: пиксель → клип-координаты
   * (с переворотом оси Y) → деление на масштаб с поправкой на соотношение сторон.
   *
   * @param px координата X в пикселях (`0` — левый край)
   * @param py координата Y в пикселях (`0` — верхний край)
   * @param viewport размеры холста в пикселях
   * @param scale масштаб отображения (по умолчанию текущий {@link getScale})
   * @returns точка диска в кадре вида
   */
  screenToDisk(px: number, py: number, viewport: Viewport, scale = this.scale): Complex {
    return projectScreenToDisk(px, py, viewport, scale);
  }

  /**
   * Перевести точку диска в кадре вида (`w = V(z)`) в пиксельные координаты
   * холста. Обратно к {@link screenToDisk}.
   *
   * @param w точка диска в кадре вида
   * @param viewport размеры холста в пикселях
   * @param scale масштаб отображения (по умолчанию текущий {@link getScale})
   */
  diskToScreen(w: Complex, viewport: Viewport, scale = this.scale): ScreenPoint {
    return projectDiskToScreen(w, viewport, scale);
  }

  /**
   * Применить перетаскивание «захват и следование»: изменить вид так, чтобы
   * точка `p0` (в текущем кадре вида) перешла в точку `p1`.
   *
   * Строится изометрия «захвата» `T` с `T(p0) = p1` через
   * {@link Geometry.viewIsometry} текущей геометрии и левым домножением на
   * текущий вид: `V' = T ∘ V`. Тогда для схваченной точки поверхности
   * `s = V⁻¹(p0)` выполняется `V'(s) = T(V(s)) = T(p0) = p1` — точка следует
   * за курсором, а модель сохраняется (Требования 4.1, 6.7).
   *
   * Для гиперболической геометрии `T` — изометрия диска SU(1,1) (поведение
   * идентично прежнему); для евклидовой — жёсткое движение плоскости, для
   * сферической — вращение сферы (SU(2)). Композиция и нормализация вида
   * выполняются в соответствующей геометрии, сохраняя модель с допуском `1e-6`.
   *
   * @param p0 схваченная точка в текущем кадре вида (обычно `screenToDisk` клика)
   * @param p1 целевая точка в текущем кадре вида (обычно `screenToDisk` курсора)
   * @returns обновлённый вид `V'`
   */
  pan(p0: Complex, p1: Complex): Mobius {
    // Изометрия «захвата» `t` (`t(p0) = p1`) в текущей геометрии, затем
    // `V' = t ∘ V`. Композиция учитывает геометрию и антиголоморфность через
    // {@link Geometry.compose}; для гиперболической/ориентируемой карты матрица
    // совпадает с прежним `normalize(compose(t, V))` (обратная совместимость).
    const t = this.geometry.viewIsometry(p0, p1);
    this.viewIso = this.geometry.compose(t, this.viewIso);
    return this.viewIso.matrix;
  }

  /**
   * Точка поверхности, отображаемая в центр холста: `s = V⁻¹(0)`.
   * Используется для проверки выхода вида за фундаментальную область.
   */
  getViewCenterSurfacePoint(): Complex {
    return this.geometry.apply(this.geometry.invert(this.viewIso), ORIGIN);
  }

  /**
   * Найти ближайшую плитку замощения, содержащую точку поверхности `s`.
   *
   * Для правильного замощения фундаментальная область совпадает с ячейкой
   * Дирихле, центрированной в начале координат, поэтому «плиткой», содержащей
   * `s`, является та копия `γ(F)`, чей центр `γ(0)` гиперболически ближе всего
   * к `s`. Тождество (сама область) выбирается, когда `s` ближе всего к центру.
   *
   * @param s точка поверхности (в координатах диска)
   * @returns ближайшая плитка (изометрия, индекс и признак тождества)
   */
  findNearestTile(s: Complex): NearestTile {
    let bestIndex = 0;
    let bestDistance = Infinity;
    for (let i = 0; i < this.neighbors.length; i++) {
      const center = this.geometry.apply(this.neighbors[i], ORIGIN);
      const d = this.geometry.distance(s, center);
      if (d < bestDistance - 1e-12) {
        bestDistance = d;
        bestIndex = i;
      }
    }
    return {
      transform: this.neighbors[bestIndex].matrix,
      index: bestIndex,
      isIdentity: bestIndex === 0,
    };
  }

  /**
   * Вернуть центр вида в фундаментальную область через преобразование склейки,
   * обеспечивая непрерывность поверхности при выходе за одну область
   * (Требование 4.2).
   *
   * Пока центр вида `s = V⁻¹(0)` попадает не в фундаментальную область, а в
   * соседнюю плитку `γ(F)`, вид заменяется на `V' = V ∘ γ`. Тогда новый центр
   * `V'⁻¹(0) = γ⁻¹(s)` лежит в `F`. Так как `γ ∈ Γ` (группа склейки), а
   * замощение и содержимое периодичны по `Γ`, изображение не меняется —
   * поверхность продолжается соседней копией непрерывно.
   *
   * Итерации повторяются, пока центр не окажется в фундаментальной области
   * (ближайшая плитка — тождество) либо не будет достигнут предел итераций.
   *
   * @returns обновлённый (нормализованный) вид `V`
   */
  normalizeView(): Mobius {
    if (this.neighbors.length <= 1) {
      return this.viewIso.matrix;
    }
    for (let iter = 0; iter < this.options.maxNormalizeIterations; iter++) {
      const s = this.getViewCenterSurfacePoint();
      const nearest = this.findNearestTile(s);
      if (nearest.isIdentity) {
        break;
      }
      // `V' = V ∘ γ` в геометрии карты; для обращающей ориентацию γ вид
      // становится антиголоморфным (зеркальным) — корректное продолжение
      // неориентируемой поверхности (Требование 8).
      this.viewIso = this.geometry.compose(this.viewIso, this.neighbors[nearest.index]);
    }
    return this.viewIso.matrix;
  }

  /**
   * Подключить обработчики мыши к холсту: перетаскивание меняет вид, после чего
   * вызывается нормализация и колбэк `onChange` (перерисовка). DOM-логика тонкая:
   * вся математика — в {@link pan}/{@link normalizeView}. Безопасно игнорируется
   * в не-браузерном окружении.
   *
   * @param canvas холст карты
   */
  attach(canvas: HTMLCanvasElement): void {
    if (typeof window === 'undefined' || !canvas) {
      return;
    }
    this.detach();
    this.canvas = canvas;
    canvas.addEventListener('pointerdown', this.pointerDownHandler);
    canvas.addEventListener('pointermove', this.pointerMoveHandler);
    window.addEventListener('pointerup', this.pointerUpHandler);
    // Колесо мыши — зум (Требование 4.3); `passive: false`, чтобы отменить
    // прокрутку страницы через preventDefault.
    canvas.addEventListener('wheel', this.wheelHandler, { passive: false });
  }

  /** Отключить обработчики мыши и сбросить состояние перетаскивания. */
  detach(): void {
    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', this.pointerDownHandler);
      this.canvas.removeEventListener('pointermove', this.pointerMoveHandler);
      this.canvas.removeEventListener('wheel', this.wheelHandler);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('pointerup', this.pointerUpHandler);
    }
    this.canvas = null;
    this.dragging = false;
    this.grabSurface = null;
  }

  /** Размеры текущего холста в пикселях (для перевода координат). */
  private viewportOf(canvas: HTMLCanvasElement): Viewport {
    return {
      width: canvas.width || canvas.clientWidth || 1,
      height: canvas.height || canvas.clientHeight || 1,
    };
  }

  /** Пиксельные координаты события мыши/указателя/колеса относительно холста. */
  private eventPixel(canvas: HTMLCanvasElement, e: MouseEvent): ScreenPoint {
    const rect = canvas.getBoundingClientRect();
    const scaleX = (canvas.width || rect.width) / (rect.width || 1);
    const scaleY = (canvas.height || rect.height) / (rect.height || 1);
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  /** Начало перетаскивания: запомнить схваченную точку поверхности. */
  private onPointerDown(e: PointerEvent): void {
    if (!this.canvas) {
      return;
    }
    const viewport = this.viewportOf(this.canvas);
    const pixel = this.eventPixel(this.canvas, e);
    const p = this.screenToDisk(pixel.x, pixel.y, viewport);
    // Схваченная точка поверхности: обратный вид от точки клика.
    this.grabSurface = this.geometry.apply(this.geometry.invert(this.viewIso), p);
    this.dragging = true;
    if (typeof this.canvas.setPointerCapture === 'function') {
      try {
        this.canvas.setPointerCapture(e.pointerId);
      } catch {
        // Некоторые окружения не поддерживают захват указателя — не критично.
      }
    }
  }

  /** Движение при перетаскивании: провести схваченную точку под курсор. */
  private onPointerMove(e: PointerEvent): void {
    if (!this.dragging || !this.canvas || !this.grabSurface) {
      return;
    }
    const viewport = this.viewportOf(this.canvas);
    const pixel = this.eventPixel(this.canvas, e);
    // Куда должна попасть схваченная точка (целевой кадр вида).
    const target = this.screenToDisk(pixel.x, pixel.y, viewport);
    // Где схваченная точка отображается сейчас.
    const current = this.geometry.apply(this.viewIso, this.grabSurface);
    this.pan(current, target);
    this.normalizeView();
    // После нормализации схваченная точка поверхности могла сменить копию —
    // пересчитать её, чтобы следующий кадр перетаскивания оставался устойчивым.
    this.grabSurface = this.geometry.apply(this.geometry.invert(this.viewIso), target);
    this.options.onChange?.(this.viewIso.matrix);
  }

  /**
   * Обработчик колеса мыши: плавно меняет масштаб с зажатием в пределы зума и
   * центрированием к курсору, затем уведомляет через `onScaleChange`/`onChange`
   * (Требования 4.3, 4.4, 4.5). Прокрутка страницы отменяется.
   */
  private onWheel(e: WheelEvent): void {
    if (!this.canvas) {
      return;
    }
    e.preventDefault();
    // Множитель масштаба: exp(-deltaY · k). deltaY > 0 (прокрутка вниз) —
    // отдаление (factor < 1), deltaY < 0 — приближение (factor > 1).
    const factor = Math.exp(-e.deltaY * ZOOM_WHEEL_SENSITIVITY);
    const pixel = this.eventPixel(this.canvas, e);
    const viewport = this.viewportOf(this.canvas);
    const before = this.scale;
    const after = this.zoomAt(factor, pixel, viewport);
    if (after !== before) {
      this.options.onScaleChange?.(after);
      this.options.onChange?.(this.viewIso.matrix);
    }
  }

  /** Завершение перетаскивания. */
  private onPointerUp(e: PointerEvent): void {
    this.dragging = false;
    this.grabSurface = null;
    if (this.canvas && typeof this.canvas.releasePointerCapture === 'function') {
      try {
        this.canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Игнорируем: указатель мог не быть захвачен.
      }
    }
  }

  /**
   * Привести переданные соседние копии к массиву обобщённых изометрий с
   * тождеством первым элементом (согласуется с {@link module:lod-manager.LodManager}).
   *
   * Принимает: `null` (только область), массив матриц {@link Mobius} или
   * изометрий {@link Isometry}, устаревший {@link SidePairingResult} (матрицы —
   * гиперболический ориентируемый путь, перечисляется прежним
   * {@link enumerateNeighbors}) или новый {@link SurfacePairing} (изометрии —
   * любая геометрия и неориентируемые склейки, перечисляется
   * {@link enumerateSurfaceNeighbors} с текущей геометрией). Всё приводится к
   * `Isometry[]` (матрицы поднимаются как сохраняющие ориентацию).
   */
  private resolveNeighbors(
    neighbors?: Mobius[] | Isometry[] | SidePairingResult | SurfacePairing | null,
  ): Isometry[] {
    if (!neighbors) {
      return [isometryIdentity()];
    }
    if (Array.isArray(neighbors)) {
      const list = neighbors.map(asIsometry);
      return list.length > 0 ? list : [isometryIdentity()];
    }
    const options = {
      maxDepth: this.options.neighborDepth,
      maxCount: this.options.maxNeighbors,
    };
    if (isSurfacePairing(neighbors)) {
      const list = enumerateSurfaceNeighbors(neighbors, options, this.geometry);
      return list.length > 0 ? list : [isometryIdentity()];
    }
    const list = enumerateNeighbors(neighbors, options).map(asIsometry);
    return list.length > 0 ? list : [isometryIdentity()];
  }
}

/**
 * Различить новый {@link SurfacePairing} (элементы `all` — {@link Isometry} с
 * полем `matrix`) и устаревший {@link SidePairingResult} (элементы `all` —
 * матрицы {@link Mobius}). Пустой `all` трактуется как устаревший.
 */
function isSurfacePairing(
  pairing: SidePairingResult | SurfacePairing,
): pairing is SurfacePairing {
  const all = (pairing as SurfacePairing).all;
  return Array.isArray(all) && all.length > 0 && 'matrix' in all[0];
}
